// The MODULE RECORD-ROW REGISTRY — "which of the app's own components draws
// ONE row of this collection?" Sibling of functions/moduleRecordRoutes.js
// ("where does a row open?"), read over the same pair of places:
//
//   collections: [{ key: 'crm_deals', route: '/crm', param: 'deal', row: 'CrmDealRecordRow' }]
//
// `workspaceProjects.js _normalizeRecordRows` validates the declarations at
// load (the component must ship with the module; key folded to lower case)
// and publishes `[{ key, row, moduleId }]` on `root.workspaceProject.recordRows`
// (the takeover module) and on each `root.workspaceApps[].recordRows` (the
// additive apps). ORDER IS THE CONTRACT, as for the routes: the takeover's
// rows first, then the apps', and the FIRST declaration of a key wins.
//
// The one consumer today is the AI answer's records list
// (functions/assistantRecords.js assistantRecordsRowOf → the mount contract in
// assistantRecordsRowMount). The shell names no app here: every app that
// declares a row gets its rows drawn its own way; every other collection keeps
// the shell's generic row.
//
// Both call forms work, like moduleRecordRoute: `moduleRecordRow(root, key)`
// (tests, shell code) and `el.call('moduleRecordRow', key)` (the element is
// `this` and the root comes from its state). Self-contained on purpose (FA204).
//
// → `{ key, row, moduleId }` for ONE collection, or null.
export function moduleRecordRow(rootOrKey, maybeKey) {
  const rootArg = rootOrKey && typeof rootOrKey === 'object' ? rootOrKey : null
  const rawKey = rootArg ? maybeKey : rootOrKey
  const key = rawKey == null ? '' : String(rawKey).trim().toLowerCase()
  if (!key) return null
  let root = rootArg
  if (!root && this && typeof this === 'object') {
    if (this.state && this.state.root) root = this.state.root
    else if (typeof this.getRootState === 'function') {
      try {
        root = this.getRootState()
      } catch (_) {
        root = null
      }
    }
  }
  if (!root || typeof root !== 'object') return null
  const lists = []
  const project = root.workspaceProject
  if (project && typeof project === 'object' && Array.isArray(project.recordRows)) {
    lists.push(project.recordRows)
  }
  if (Array.isArray(root.workspaceApps)) {
    for (const app of root.workspaceApps) {
      if (app && typeof app === 'object' && Array.isArray(app.recordRows))
        lists.push(app.recordRows)
    }
  }
  for (const rows of lists) {
    for (const r of rows) {
      if (!r || typeof r !== 'object') continue
      const k = r.key == null ? '' : String(r.key).trim().toLowerCase()
      const row = typeof r.row === 'string' ? r.row.trim() : ''
      const moduleId = r.moduleId == null ? '' : String(r.moduleId)
      // A half row (a stash from an older bundle, a dev override) never mounts.
      if (k !== key || !row || !moduleId) continue
      return { key: k, row, moduleId }
    }
  }
  return null
}
